import React from "react";
import moment from "moment";
import numeral from "numeral";

import { Button, Icon } from "aible";

import Toggle from "react-toggle";
import Slider, { Range, Handle } from "rc-slider";
import Select from "react-select";

import { DropdownButton, MenuItem, Popover, OverlayTrigger } from "react-bootstrap";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";

import RefreshIcon from "images/refreshB.svg";

import { LineInput } from "components/Inputs/LineInput";

import PredictionChart from "containers/ForecastChart/PredictionChart";
import TrendChart from "containers/ForecastChart/TrendChart";
import DateRangeSelect from "containers/ForecastChart/DateRangeSelect";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import ModelError from "components/Results/ModelError";

import MetricsTable from "components/UnifiedProject/Forecast/MetricsTable";

import styles from "./style.module.css";

const DEFAULT_MODELS_SHOW = 12;

const TooltipSlider = Slider.createSliderWithTooltip(Slider);

export default class Forecast extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      forecastLocalRatioTimer: null,
      tempForecastLocalRatio: this.props.currentRatio,
      showMetrics: false,
      showTooltip: false
    };
  }

  handleUpdateForecastRatio(val, skipWait = false) {
    const { updateForecastLocalRatio, useForecastLocalRatio, updateUseForecastLocalRatio } = this.props;
    const { forecastLocalRatioTimer, tempForecastLocalRatio } = this.state;

    const scaledVal = Math.pow(10, Number(val));
    const retVal = isNaN(scaledVal) ? tempForecastLocalRatio : scaledVal;

    if (forecastLocalRatioTimer !== null) {
      clearTimeout(forecastLocalRatioTimer);
    }

    if (!useForecastLocalRatio) {
      updateUseForecastLocalRatio(true);
    }

    if (skipWait) {
      this.setState({
        tempForecastLocalRatio: Number(retVal),
        forecastLocalRatioTimer: null
      });
      return updateForecastLocalRatio(retVal);
    } else {
      const tempPointer = setTimeout(() => {
        this.setState({
          forecastLocalRatioTimer: null
        });
        updateForecastLocalRatio(retVal);
      }, 100);
      this.setState({
        tempForecastLocalRatio: retVal,
        forecastLocalRatioTimer: tempPointer
      });
    }
  }

  renderControls() {
    const {
      forecastPredictionPlot,
      setupLabels,
      updateUseForecastLocalRatio,
      updateForecastLocalRatio,
      forecastLocalRatio,
      useForecastLocalRatio,
      downloadForecast,
      isForecastLoading,
      forecastQuantile
    } = this.props;

    const { tempForecastLocalRatio, showMetrics } = this.state;

    if (showMetrics) {
      return (
        <div className={styles.forecastControls}>
          <Button
            onClick={() => this.setState({ showMetrics: false })}
            type="secondary"
            className={styles.exportCSVButton}
          >
            {"Hide Advanced Metrics"}
          </Button>
        </div>
      );
    }

    const underPredictProbability = 1 - forecastQuantile.quantile;
    const overPredictProbability = forecastQuantile.quantile;

    // If we want the colors to pivot around the center, we're going to have to use a range with a value locked to 0
    return (
      <div className={styles.forecastControls}>
        {this.renderModelSelection()}
        <div className={styles.forecastSliderWrapper}>
          <div className={styles.forecastSliderLabelWrapper}>
            <div className={styles.forecastSliderLabel}>{"Higher " + setupLabels.underPredictAction}</div>
            <div className={styles.forecastSliderLabel}>{"Higher " + setupLabels.overPredictAction}</div>
          </div>
          <div className={styles.forecastSlider}>
            <TooltipSlider
              min={-1}
              max={1}
              onChange={(e) => this.handleUpdateForecastRatio(e)}
              value={Math.log10(tempForecastLocalRatio)}
              step={0.01}
              trackStyle={{ backgroundColor: "#7E69D700" }}
              handleStyle={{
                backgroundColor: "#7E69D7",
                borderColor: "white",
                boxShadow: "0px 0px 6px 1px rgba(81, 47, 119, 0.35)",
                zIndex: "5"
              }}
              tipProps={{
                placement: "top",
                prefixCls: "rc-slider-tooltip"
              }}
              tipFormatter={(value) => {
                return (
                  <div className={styles.forecastSliderTooltip}>
                    {`${setupLabels.overPredictAction}/${setupLabels.underPredictAction} Ratio: ` +
                      numeral(Math.pow(10, Number(value))).format("0.[00]")}
                  </div>
                );
              }}
            />
          </div>
        </div>
        <div className={styles.forecastInfo}>
          <OverlayTrigger
            placement="top"
            trigger={["hover", "click"]}
            rootClose
            overlay={
              <Popover id="popover-positioned-top">
                <div className={styles.toolTipElem + " " + styles.quantileToolTip}>
                  <span>{setupLabels.overPredictAction + " Probability: "}</span>
                  <strong>{numeral(overPredictProbability * 100).format("0.[0]") + "%"}</strong>
                </div>
                <div className={styles.toolTipElem + " " + styles.quantileToolTip}>
                  <span>{setupLabels.underPredictAction + " Probability: "}</span>
                  <strong>{numeral(underPredictProbability * 100).format("0.[0]") + "%"}</strong>
                </div>
              </Popover>
            }
          >
            <FontAwesomeIcon icon={faInfo} className={styles.typeIcon} />
          </OverlayTrigger>
        </div>
        <div className={styles.exportWrapper}>
          <Button
            onClick={() => downloadForecast()}
            type="secondary"
            className={styles.exportCSVButton}
            disabled={isForecastLoading}
          >
            {"Export CSV"}
          </Button>
        </div>
        <div className={styles.dropdownWrapper}>
          <DropdownButton pullRight id="options-dropdown-button" title="" style={{ margin: "3px 10px 3px 20px" }}>
            <MenuItem onClick={() => this.setState({ showMetrics: true })}>{"Advanced Model Metrics"}</MenuItem>
          </DropdownButton>
        </div>
      </div>
    );
  }

  renderForecastFetch() {
    const {
      models,
      model,
      fetchForecast,
      forecastTime,
      forecastPlot,
      updateForecastTime,
      forecastTimeOptions,
      useForecastLocalRatio,
      forecastTimeUnit,
      updateForecastTimeUnit
    } = this.props;

    return (
      <React.Fragment>
        <div className={styles.fHeader}>
          <span>{"When do you plan to take an action on your forecast?"}</span>
        </div>
        <div className={styles.showAll}>
          <LineInput
            value={forecastTime}
            onChange={(v) => updateForecastTime(v)}
            isMediumBox
            inputStyle={{ fontSize: "13px", maxWidth: "75px" }}
          />
          <div className={styles.forecastTimeUnit}>
            <Select
              placeholder="Select Time Unit"
              options={forecastTimeOptions}
              value={{ value: forecastTimeUnit, label: forecastTimeUnit }}
              onChange={(v) => {
                updateForecastTimeUnit(v.value);
              }}
              style={{ marginTop: "6px" }}
            />
          </div>
          <Button
            type={"primary"}
            style={{ marginLeft: "15px" }}
            onClick={() => {
              return fetchForecast();
            }}
          >
            {"Get Forecast"}
          </Button>
        </div>
      </React.Fragment>
    );
  }

  selectModelAtIndex(index) {
    const { updateForecastModelSelection, forecastModelOptions } = this.props;

    const retIndex = Math.max(0, Math.min(index, forecastModelOptions.length - 1));

    const retModelOption = forecastModelOptions[retIndex];

    return updateForecastModelSelection(retModelOption.value);
  }

  renderModelSelection() {
    const {
      fetchForecast,
      forecastModelSelection,
      forecastModelOptions,
      updateForecastModelSelection,
      downloadForecast,
      isForecastLoading
    } = this.props;

    const selectedValueIndex = forecastModelOptions.findIndex((m) => m.value === forecastModelSelection);
    const selectedValue = forecastModelOptions[selectedValueIndex];

    const retOptions = forecastModelOptions;

    return (
      <div className={styles.resultsSubHeader}>
        <div className={styles.modelSelectWrapper}>
          <Button
            onClick={() => this.selectModelAtIndex(selectedValueIndex - 1)}
            type="secondary"
            className={styles.prevModelButton}
            disabled={selectedValueIndex <= 0}
          >
            {"<"}
          </Button>
          <Select
            value={selectedValue}
            onChange={(m) => {
              return updateForecastModelSelection(m.value);
            }}
            options={retOptions}
            placeholder={"Select model"}
            className={styles.modelSelect}
            styles={{ control: (base) => ({ ...base, borderRadius: 0 }) }}
          />
          <Button
            onClick={() => this.selectModelAtIndex(selectedValueIndex + 1)}
            type="secondary"
            className={styles.nextModelButton}
            disabled={selectedValueIndex >= forecastModelOptions.length - 1}
          >
            {">"}
          </Button>
        </div>
      </div>
    );
  }

  renderMetricsTable() {
    const { modelMetrics, forecastModelSelection, forecastModelOptions, updateForecastModelSelection } = this.props;

    const { showMetrics } = this.state;

    return (
      <div className={styles.metricsTableWrapper + " " + (showMetrics ? "" : styles.hide)}>
        <MetricsTable
          data={forecastModelOptions}
          selection={forecastModelSelection}
          updateModelSelection={updateForecastModelSelection}
        />
      </div>
    );
  }

  renderForecastResults() {
    const { id, projectId, optimized, isError, errorMessages, project, isTraining, setupLabels, isForecastLoading } =
      this.props;

    if (isForecastLoading) {
      return (
        <div className={styles.loadingResults}>
          <LoadingIndicator
            style={{
              margin: "0px",
              marginTop: "100px",
              marginBottom: "20px"
            }}
          />
          <div className={styles.loadingResultsTitle}>{"Loading Forecasting Results..."}</div>
        </div>
      );
    }

    return (
      <div className={styles.resultsContent}>
        <div className={styles.mainChart}>
          <PredictionChart id={id} projectId={projectId} />
        </div>
        <div className={styles.chartTitle}>{"Trend and Seasonality"}</div>
        <div className={styles.mainChart + " " + styles.secondaryChart}>
          <TrendChart id={id} projectId={projectId} />
        </div>
        <div className={styles.chartControls}>
          <DateRangeSelect id={id} projectId={projectId} />
        </div>
      </div>
    );
  }

  render() {
    const {
      id,
      projectId,
      optimized,
      isError,
      errorMessages,
      project,
      isTraining,
      setupLabels,
      isForecastLoading,
      selectedDataset
    } = this.props;
    const { showMetrics } = this.state;

    if (isError) {
      return <ModelError errors={errorMessages} />;
    }

    if (optimized === undefined || optimized.isLoading || optimized.isCreating) {
      return (
        <div className={styles.optimizerEmpty}>
          <div className={styles.loading}>
            <LoadingIndicator
              style={{
                margin: "0px",
                marginTop: "-20%",
                marginBottom: "50px",
                transform: "scale(1.75)"
              }}
            />
            <div className={styles.loadMsg}>
              {(optimized && optimized.isCreating) || isTraining
                ? "Training Forecasting Models..."
                : "Loading Forecasting Models..."}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.results}>
        <div className={styles.pageHeader}>
          <div className={styles.pageTitle} style={{ padding: "20px", paddingBottom: "0px" }}>
            {showMetrics
              ? "Forecast: Advanced Model Metrics"
              : "Forecast for " + (selectedDataset?.outcome ?? setupLabels.numberPredicted)}
          </div>
          {this.renderControls()}
        </div>
        {showMetrics ? this.renderMetricsTable() : this.renderForecastResults()}
      </div>
    );
  }
}
